/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / CUSTOMER-NEWSLETTER — Double-Opt-in-Abo für anonyme Gäste (S226 CUT C)
   =============================================================================
   Behandelt drei URL-Hashes (Pivot 2026-07-08, Einwilligungs-Modell):
     • #newsletter/<rid>            — Landing: E-Mail eintragen + anmelden
     • #newsletter-confirm/<token>  — DOI-Bestätigung (pending → confirmed)
     • #newsletter-unsub/<token>    — One-Click-Abmeldung

   ⚠ PREFETCH-SICHER (wie customer-cancel.js, Finding M-09): der GET-Pfad rendert
   NUR eine Card. Die Mutation (confirm/unsubscribe/subscribe) läuft AUSSCHLIESSLICH
   auf expliziten Button-Klick — Mail-Clients (Gmail/Apple Mail/GMX/Scanner)
   prefetchen Links, ein Auto-Confirm/-Unsub im GET-Pfad wäre ein Bug (falsch-
   bestätigte Einwilligung / versehentliche Abmeldung). NIEMALS eine RPC hier im
   render*-Pfad aufrufen.

   Alle drei RPCs sind SECURITY DEFINER, Token = Autorisierung (kein auth.uid()).
   subscribe antwortet IMMER neutral (kein Adress-Oracle) — die UI zeigt darum
   unabhängig vom Ergebnis dieselbe „Bestätigungsmail unterwegs"-Meldung.
============================================================================ */

import { htmlEscape } from '../utils/helpers.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

function supa() {
  return (typeof window !== 'undefined' && window.App && window.App.supabase) || null;
}

/**
 * Pure — parst den Newsletter-Hash in eine Route.
 *   #newsletter/<rid>            → { mode:'landing',  rid }
 *   #newsletter-confirm/<token>  → { mode:'confirm',  token }
 *   #newsletter-unsub/<token>    → { mode:'unsub',    token }
 *   sonst                        → null
 * @param {string} hash window.location.hash
 */
export function _parseNewsletterHash(hash) {
  if (typeof hash !== 'string' || hash.length === 0) return null;
  const dec = (s) => { try { const d = decodeURIComponent(s); return d && d.length ? d : null; } catch (_) { return null; } };
  let m = hash.match(/^#newsletter-confirm\/(.+)$/);
  if (m) { const token = dec(m[1]); return token ? { mode: 'confirm', token } : null; }
  m = hash.match(/^#newsletter-unsub\/(.+)$/);
  if (m) { const token = dec(m[1]); return token ? { mode: 'unsub', token } : null; }
  m = hash.match(/^#newsletter\/(.+)$/);
  if (m) { const rid = dec(m[1]); return rid ? { mode: 'landing', rid } : null; }
  return null;
}

/** Pure — grobe E-Mail-Plausibilität (Client-Vorprüfung; Server ist autoritativ). */
export function _looksLikeEmail(v) {
  return typeof v === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());
}

/** Wenn ein Newsletter-Hash vorliegt: View wechseln + Card rendern (rein lesend). */
export function checkNewsletterRequestFromUrl() {
  const route = _parseNewsletterHash(window.location.hash);
  if (!route) return false;
  if (typeof window.switchView === 'function') window.switchView('newsletter');
  renderNewsletterView(route);
  return true;
}

function _card() { return document.getElementById('newsletter-card'); }

/** Rendert die passende Card für die Route (kein Seiteneffekt außer DOM). */
export function renderNewsletterView(route) {
  const wrap = _card();
  if (!wrap) return;
  if (!route) { wrap.innerHTML = _invalidCard(); return; }
  if (route.mode === 'landing') { wrap.innerHTML = _landingCard(route.rid); return; }
  if (route.mode === 'confirm') { wrap.innerHTML = _confirmPromptCard(route.token); return; }
  if (route.mode === 'unsub')   { wrap.innerHTML = _unsubPromptCard(route.token); return; }
  wrap.innerHTML = _invalidCard();
}

function _brandName() {
  const s = getState().settings || {};
  return htmlEscape(s.restaurantName || 'unser Restaurant');
}

function _invalidCard() {
  return `
    <h2>Link ungültig</h2>
    <p>Dieser Newsletter-Link ist leider nicht gültig.</p>
    <a class="btn btn-secondary" href="#" onclick="closeNewsletterView();return false;">Zur Startseite</a>`;
}

function _landingCard(rid) {
  return `
    <h2>Newsletter abonnieren</h2>
    <p>Bleib auf dem Laufenden über Neuigkeiten und Angebote von ${_brandName()}.</p>
    <div class="field" style="margin:14px 0;">
      <label for="nl-email" style="display:block;margin-bottom:6px;color:var(--ink-soft);">Deine E-Mail-Adresse</label>
      <input type="email" id="nl-email" autocomplete="email" placeholder="name@example.com" style="width:100%;">
    </div>
    <p style="color:var(--ink-soft);font-size:.85rem;line-height:1.5;">
      Mit dem Klick auf „Anmelden" erhältst du eine Bestätigungsmail (Double-Opt-in).
      Erst nach deiner Bestätigung senden wir dir Neuigkeiten. Du kannst dich jederzeit
      kostenlos mit einem Klick wieder abmelden.
    </p>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-primary" onclick="newsletterSubmitSubscribe('${htmlEscape(rid)}')">Anmelden</button>
      <button class="btn btn-secondary" onclick="closeNewsletterView()">Zurück</button>
    </div>`;
}

function _confirmPromptCard(token) {
  return `
    <h2>Newsletter-Abo bestätigen</h2>
    <p>Bitte bestätige mit einem Klick, dass du den Newsletter von ${_brandName()} abonnieren möchtest.</p>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-primary" onclick="newsletterConfirm('${htmlEscape(token)}')">Abo bestätigen</button>
      <button class="btn btn-secondary" onclick="closeNewsletterView()">Abbrechen</button>
    </div>`;
}

function _unsubPromptCard(token) {
  return `
    <h2>Newsletter abbestellen</h2>
    <p>Möchtest du den Newsletter von ${_brandName()} wirklich abbestellen? Deine Bestell- und
       Reservierungsmails bleiben davon unberührt.</p>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-danger" onclick="newsletterUnsubscribe('${htmlEscape(token)}')">Ja, abbestellen</button>
      <button class="btn btn-secondary" onclick="closeNewsletterView()">Doch nicht</button>
    </div>`;
}

/** Anmeldung — subscribe-RPC, IMMER neutrale Bestätigung (kein Oracle). */
export async function newsletterSubmitSubscribe(rid) {
  const wrap = _card();
  const input = document.getElementById('nl-email');
  const email = input ? String(input.value || '').trim() : '';
  if (!_looksLikeEmail(email)) {
    const hint = document.getElementById('nl-hint');
    if (!hint && input && input.parentNode) {
      const p = document.createElement('p');
      p.id = 'nl-hint';
      p.style.cssText = 'color:var(--danger,#c8102e);font-size:.85rem;margin-top:6px;';
      p.textContent = 'Bitte gib eine gültige E-Mail-Adresse ein.';
      input.parentNode.appendChild(p);
    }
    if (input) input.focus();
    return;
  }
  const client = supa();
  try {
    if (client) {
      await client.rpc('newsletter_subscribe', { p_restaurant_id: rid, p_email: email, p_source: 'landing' });
    }
  } catch (e) {
    // Neutral: auch bei Fehler keine Detail-Preisgabe (kein Oracle). Loggen reicht.
    console.error('[newsletterSubmitSubscribe]', e);
  }
  if (wrap) {
    wrap.innerHTML = `
      <h2>Fast geschafft!</h2>
      <p>Falls nötig, ist eine Bestätigungsmail zu <strong>${htmlEscape(email)}</strong> unterwegs.
         Bitte klicke den Link darin, um dein Abo zu aktivieren.</p>
      <p style="color:var(--ink-soft);font-size:.85rem;">Schau ggf. auch im Spam-Ordner nach.</p>
      <a class="btn btn-primary" href="#" onclick="closeNewsletterView();return false;">Zur Startseite</a>`;
  }
}

/** DOI-Bestätigung — nur auf expliziten Klick. */
export async function newsletterConfirm(token) {
  const wrap = _card();
  const client = supa();
  let ok = false;
  try {
    if (client) {
      const { data } = await client.rpc('newsletter_confirm_by_token', { p_token: token });
      ok = !!(data && data.ok);
    }
  } catch (e) { console.error('[newsletterConfirm]', e); }
  if (!wrap) return;
  wrap.innerHTML = ok
    ? `<h2>Abo bestätigt ✓</h2>
       <p>Vielen Dank! Du erhältst ab jetzt unseren Newsletter. Du kannst dich jederzeit
          über den Link in jeder Mail wieder abmelden.</p>
       <a class="btn btn-primary" href="#" onclick="closeNewsletterView();return false;">Zur Startseite</a>`
    : `<h2>Link abgelaufen</h2>
       <p>Dieser Bestätigungs-Link ist ungültig oder wurde bereits verwendet. Falls du dein Abo
          noch aktivieren möchtest, melde dich bitte erneut an.</p>
       <a class="btn btn-secondary" href="#" onclick="closeNewsletterView();return false;">Zur Startseite</a>`;
}

/** One-Click-Abmeldung — nur auf expliziten Klick. */
export async function newsletterUnsubscribe(token) {
  const wrap = _card();
  const client = supa();
  let ok = false;
  try {
    if (client) {
      const { data } = await client.rpc('newsletter_unsubscribe_by_token', { p_token: token });
      ok = !!(data && data.ok);
    }
  } catch (e) { console.error('[newsletterUnsubscribe]', e); }
  if (!wrap) return;
  wrap.innerHTML = ok
    ? `<h2>Abgemeldet</h2>
       <p>Du erhältst ab sofort keinen Newsletter mehr. Deine Bestell- und Reservierungsmails
          bekommst du weiterhin wie gewohnt.</p>
       <a class="btn btn-primary" href="#" onclick="closeNewsletterView();return false;">Zur Startseite</a>`
    : `<h2>Link ungültig</h2>
       <p>Dieser Abmelde-Link ist nicht gültig. Falls du weiter Newsletter erhältst, nutze bitte
          den Abmelde-Link aus einer aktuellen Mail.</p>
       <a class="btn btn-secondary" href="#" onclick="closeNewsletterView();return false;">Zur Startseite</a>`;
}

/**
 * S226 — Web-Form-Abo (Häkchen in Reservierung/Takeaway/Lieferung). NICHT-FATAL:
 * ein Fehler hier darf die Bestellung/Reservierung NIE blockieren. Double-Opt-in
 * greift trotzdem (subscribe enqueued nur die Bestätigungsmail; erst deren Klick
 * bestätigt). Kein await-Zwang beim Caller nötig — fire-and-forget möglich.
 * @param {string} rid Restaurant-ID
 * @param {string} email Gast-E-Mail
 * @param {string} source web_reservation | web_order | web_delivery
 */
export async function newsletterSubscribeFromWebForm(rid, email, source) {
  try {
    const client = supa();
    const mail = String(email || '').trim();
    if (!client || !rid || !_looksLikeEmail(mail)) return;
    await client.rpc('newsletter_subscribe', { p_restaurant_id: rid, p_email: mail, p_source: source || 'web_order' });
  } catch (e) {
    // bewusst geschluckt — Abo ist Beiwerk, nie Blocker für den Hauptvorgang.
    console.warn('[newsletterSubscribeFromWebForm]', e && e.message);
  }
}

/** Liest ein Abo-Häkchen aus dem DOM und meldet bei Häkchen nicht-fatal an. */
export function maybeSubscribeFromCheckbox(checkboxId, rid, email, source) {
  try {
    const box = document.getElementById(checkboxId);
    if (box && box.checked) newsletterSubscribeFromWebForm(rid, email, source);
  } catch (_) { /* nie fatal */ }
}

/** Zurück zum Customer-Form. */
export function closeNewsletterView() {
  history.replaceState(null, '', window.location.pathname + window.location.search);
  if (typeof window.switchView === 'function') window.switchView('customer');
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    checkNewsletterRequestFromUrl,
    renderNewsletterView,
    newsletterSubmitSubscribe,
    newsletterConfirm,
    newsletterUnsubscribe,
    newsletterSubscribeFromWebForm,
    maybeSubscribeFromCheckbox,
    closeNewsletterView,
  });
}
